/**
 * @file Racine du rendu Three.js : WebGLRenderer, scene, lumieres, camera, sous-rendus (sol, entites, FX), resize/DPR, projection pour l'UI, dispose complet.
 */
import { Color, Scene, Vector3, WebGLRenderer } from 'three';
import type { ConfigIndex, GameConfig } from '@config/index';
import type { AmmoStyle, FireworkStyle } from '@config/types';
import type { WeaponBehavior } from '@shared/enums';
import { HUB_SEASONS, RENDER } from '@config/index';
import type { EventBus, GameEvents, SimView } from '@core/index';
import { CameraRig } from './CameraRig';
import { addGameLights, applyLightGrade } from './Lighting';
import { FpsGuard } from './FpsGuard';
import { PostFx } from './PostFx';
import { CatFactory } from './factories/CatFactory';
import { GunFactory } from './factories/GunFactory';
import { TargetFactory } from './factories/TargetFactory';
import { AuraRenderer } from './fx/AuraRenderer';
import { AuraRigRenderer } from './fx/AuraRig';
import { RemoteAuras, type RemoteAuraEntry } from './fx/RemoteAuras';
import { ShotFxRenderer } from './fx/ShotFxRenderer';
import type { ShotFxStyle } from '@config/types';
import { FxManager } from './fx/FxManager';
import { PlacesFx } from './fx/PlacesFx';
import { SeasonAmbience } from './fx/SeasonAmbience';
import { GeometryLibrary } from './gpu/GeometryLibrary';
import { ResourceTracker } from './gpu/ResourceTracker';
import type { Projector, ScreenPoint } from './Projector';
import { ToonKit } from './toon/toon';
import { EntityViews } from './views/EntityViews';
import type { RemoteCat } from './views/EntityViews';
import { GiantTrees } from './views/GiantTrees';
import { GroundRenderer } from './views/GroundRenderer';
import { InstancedTargets } from './views/InstancedTargets';
import { ProjectileRenderer } from './views/ProjectileRenderer';

/** Au-dela de cet ecart (u) entre la camera et le chef, on recadre sans travelling (teleportation). */
const CAMERA_SNAP_DISTANCE = 30;

export interface RendererOptions {
  canvas: HTMLCanvasElement;
  config: GameConfig;
  index: ConfigIndex;
  bus: EventBus<GameEvents>;
  weaponDefOf: (uid: string) => string | null;
  /** Style visuel actif des munitions d'une categorie d'arme (skin de munitions equipe) : lu a la volee par ProjectileRenderer / FxManager. */
  ammoStyleOf: (behavior: WeaponBehavior) => AmmoStyle;
  eco: boolean;
}

export interface GpuMemoryInfo {
  geometries: number;
  textures: number;
  programs: number;
  tracked: number;
  drawCalls: number;
}

export class GameRenderer implements Projector {
  readonly renderer: WebGLRenderer;
  readonly scene = new Scene();
  readonly cameraRig: CameraRig;
  readonly res = new ResourceTracker();
  readonly kit: ToonKit;
  readonly geo: GeometryLibrary;
  private readonly views: EntityViews;
  private readonly ground: GroundRenderer;
  private readonly fx: FxManager;
  private readonly aura: AuraRenderer;
  private readonly auraRig: AuraRigRenderer;
  private readonly shotFx: ShotFxRenderer;
  /** FX de tir equipe (lu a chaque frame depuis la vue ; les evenements du core s'y refèrent). */
  private shotStyle: ShotFxStyle | null = null;
  private readonly remoteAuras: RemoteAuras;
  /** Aura de chaque joueur distant (id -> id d'aura), d'apres son apparence. */
  private remoteAuraIds = new Map<string, string>();
  private readonly remoteAuraList: RemoteAuraEntry[] = [];
  /** Effets cosmetiques (aura, FX de tir, feux d'artifice) : les miens / ceux des autres joueurs (reglages). */
  private fxSelf = true;
  private fxOthers = true;
  private readonly places: PlacesFx;
  private readonly ambience: SeasonAmbience;
  private readonly projectiles: ProjectileRenderer;
  private readonly guns: GunFactory;
  private cssW = 1;
  private cssH = 1;
  private eco: boolean;
  /** Qualite choisie dans les reglages : 'high' (defaut) autorise le bloom si on n'est pas en eco. */
  private quality: 'low' | 'high' = 'high';
  /** Dernier devicePixelRatio recu (pour reappliquer le plafond quand la qualite / l'eco changent). */
  private devicePr = 1;
  private readonly post: PostFx;
  private readonly lights: ReturnType<typeof addGameLights>;
  /** Mesure la fluidite tant que le bloom est actif ; s'il rame durablement, le bloom est coupe pour la session. */
  private readonly fpsGuard = new FpsGuard();
  /** Bloom coupe automatiquement (session seulement : le reglage sauvegarde n'est pas modifie). */
  private autoLow = false;
  /** Appele une fois quand le bloom est coupe automatiquement (toast d'information). */
  onAutoLowQuality: (() => void) | null = null;
  private lastBiome = '';
  private lastMode = '';
  private snapNext = true;
  private readonly tmp = new Vector3();

  constructor(private readonly opts: RendererOptions) {
    this.eco = opts.eco;
    this.renderer = new WebGLRenderer({ canvas: opts.canvas, antialias: !opts.eco, powerPreference: 'high-performance' });
    this.renderer.setClearColor(new Color('#cdeefe'));
    this.cameraRig = new CameraRig(1);
    this.kit = new ToonKit(this.res);
    this.geo = new GeometryLibrary(this.res);

    // lumieres normalisees (three >= r155) : partagees avec la galerie de dev (render/Lighting.ts)
    this.lights = addGameLights(this.scene);

    this.ground = new GroundRenderer(this.scene, this.kit, this.res);
    const targetFactory = new TargetFactory(this.geo, this.res);
    const targets = new InstancedTargets(this.scene, targetFactory, this.kit, this.res);
    const giants = new GiantTrees(this.scene, targetFactory, this.kit, this.res);
    this.places = new PlacesFx(this.scene, this.kit, this.geo);
    this.ambience = new SeasonAmbience(this.scene, this.kit, this.geo);
    this.ambience.setEco(opts.eco);
    const projectiles = new ProjectileRenderer(this.scene, this.kit, this.geo, opts.ammoStyleOf);
    this.projectiles = projectiles;
    this.guns = new GunFactory(this.kit, this.geo);
    this.views = new EntityViews(this.scene, opts.index, new CatFactory(this.kit, this.geo), this.guns, targets, projectiles, this.ground, giants, this.places, opts.weaponDefOf);
    this.fx = new FxManager(this.scene, this.kit, this.geo, this.cameraRig, opts.ammoStyleOf);
    this.fx.setEco(opts.eco);
    this.aura = new AuraRenderer(this.scene, this.kit, this.geo);
    this.aura.setEco(opts.eco);
    this.auraRig = new AuraRigRenderer(this.scene);
    this.auraRig.setEco(opts.eco);
    this.remoteAuras = new RemoteAuras(this.scene, this.kit, this.geo);
    this.remoteAuras.setEco(opts.eco);
    this.shotFx = new ShotFxRenderer(this.scene);
    this.shotFx.setEco(opts.eco);
    this.shotFx.bind(
      () => this.shotStyle,
      (id) => this.views.muzzleWorld(id),
    );
    this.shotFx.subscribe(opts.bus);
    projectiles.setEco(opts.eco);
    projectiles.trailSink = (style, x, y, z, dx, dz) => {
      if (style.trail !== 'none') this.fx.trail(style, x, y, z, dx, dz);
      this.shotFx.trail(x, y, z, dx, dz);
    };
    this.fx.subscribe(opts.bus, (id) => this.views.muzzleWorld(id));
    this.post = new PostFx(this.renderer, this.scene, this.cameraRig.camera, opts.canvas);
    this.post.setEnabled(this.wantsPost());
  }

  private wantsPost(): boolean {
    return !this.eco && RENDER.quality[this.quality].bloom && !this.autoLow;
  }

  /**
   * Qualite graphique (reglages, RENDER.quality) : 'low' = rendu a 1x (au lieu de 2x), sans halo, ~65 % des particules ; 'high' = tout.
   * Avant, seul le halo changeait (et il ne se voit que sur les impacts) : le reglage paraissait sans effet.
   */
  setQuality(quality: 'low' | 'high'): void {
    // un choix explicite du joueur rearme le garde-fou de fluidite
    this.quality = quality;
    this.autoLow = false;
    this.fpsGuard.reset();
    this.fx.setDetail(RENDER.quality[quality].fxScale);
    this.post.setEnabled(this.wantsPost());
    this.resize(this.cssW, this.cssH, this.devicePr);
  }

  resize(cssWidth: number, cssHeight: number, devicePixelRatio: number): void {
    this.cssW = Math.max(1, cssWidth);
    this.cssH = Math.max(1, cssHeight);
    this.devicePr = devicePixelRatio;
    const cap = Math.min(RENDER.camera.maxDpr, this.eco ? RENDER.quality.low.maxDpr : RENDER.quality[this.quality].maxDpr);
    const dpr = Math.min(cap, devicePixelRatio);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(this.cssW, this.cssH, false);
    this.cameraRig.resize(this.cssW / this.cssH);
    this.kit.setResolution(this.cssW * dpr, this.cssH * dpr);
    this.post.setSize(this.cssW, this.cssH, dpr);
  }

  /** Autres joueurs du hub social (rendu seul, hors simulation) ; liste vide = aucun. */
  setRemoteCats(list: readonly RemoteCat[]): void {
    this.views.setRemoteCats(list);
    this.remoteAuraIds = new Map();
    for (const c of list) if (c.auraId) this.remoteAuraIds.set(c.id, c.auraId);
  }

  /**
   * Visibilite des effets cosmetiques : `self` = mon aura, mes FX de tir, mes feux d'artifice ; `others` = auras et feux d'artifice des
   * autres joueurs. Les retours de jeu (impacts, chiffres de degats, alertes) restent toujours affiches.
   */
  setFxVisibility(self: boolean, others: boolean): void {
    this.fxSelf = self;
    this.fxOthers = others;
    this.fx.ownFireworks = self;
  }

  /** Feu d'artifice d'un autre joueur (ignore si « effets des autres » est coupe). */
  remoteFirework(x: number, y: number, style: AmmoStyle, show: FireworkStyle): void {
    if (this.fxOthers) this.fx.remoteFirework(x, y, style, show);
  }

  setEco(eco: boolean): void {
    this.eco = eco;
    this.fx.setEco(eco);
    this.aura.setEco(eco);
    this.auraRig.setEco(eco);
    this.remoteAuras.setEco(eco);
    this.shotFx.setEco(eco);
    this.ambience.setEco(eco);
    this.projectiles.setEco(eco);
    this.post.setEnabled(this.wantsPost());
    this.resize(this.cssW, this.cssH, this.devicePr);
  }

  /**
   * Une frame d'affichage. `alpha` = fraction du tic courant (interpolation), `dt` = temps reel ecoule (animations, FX).
   * Les changements de biome / mode declenchent la (re)construction du sol et la liberation des portees GPU correspondantes.
   */
  render(view: SimView, alpha: number, dt: number, time: number): void {
    // le hub a son propre decor de saison (sol / ciel / brouillard) et n'herite pas du biome de la partie
    const look = view.mode === 'hub' ? `hub:${view.season}` : view.biome;
    if (look !== this.lastBiome) {
      this.lastBiome = look;
      applyLightGrade(this.lights, look);
      if (view.mode === 'hub') {
        const s = HUB_SEASONS[view.season];
        this.ground.setBiome({ id: look, ground: s.ground, sky: s.sky, fog: s.fog });
      } else {
        this.ground.setBiome(this.opts.config.biomes[view.biome]);
      }
    }
    if (view.mode !== this.lastMode) {
      this.lastMode = view.mode;
      const monster = view.mode === 'monster' ? this.opts.config.modes.monster.params.barrierRadius : null;
      this.ground.setArena(view.arena, monster);
      this.cameraRig.fitWidth = view.rules.cameraFitWidth ?? RENDER.camera.fitWidth;
      this.cameraRig.yawDeg = view.rules.cameraYawDeg ?? RENDER.camera.yawDeg;
      this.cameraRig.follow = view.rules.cameraFollow;
      this.cameraRig.setTarget(0, 0, true);
      this.cameraRig.setZoom(view.zoom, dt, true);
      this.snapNext = true;
    }
    // zoom du hub (1 hors hub) : cadrage lisse, le brouillard suit la distance de vue
    this.cameraRig.setZoom(view.zoom, dt);
    const hud = view.hud();
    if (hud?.barrier) this.ground.setBarrier(hud.barrier.hp / hud.barrier.max);
    if (this.cameraRig.follow) {
      const leaderId = view.catIds[0];
      const leader = leaderId !== undefined ? view.entity(leaderId) : undefined;
      if (leader) {
        this.cameraRig.setTarget(leader.transform.x, -leader.transform.y, this.snapNext);
        // Teleportation (entree de mode, recentrage du chemin) : recadrage instantane plutot qu'un long travelling.
        if (this.cameraRig.lag > CAMERA_SNAP_DISTANCE) this.cameraRig.setTarget(leader.transform.x, -leader.transform.y, true);
        this.snapNext = false;
      }
    }
    this.ground.follow(this.cameraRig.focusX, this.cameraRig.focusZ);
    this.ground.setViewDistance(this.cameraRig.viewDistance);
    this.views.setCamera(this.cameraRig.camera.quaternion);
    this.views.sync(view, alpha, dt, time);
    this.places.update(time, view.mode === 'hub' && view.season === 'winter');
    this.ambience.update(view.mode === 'hub' ? view.season : null, this.cameraRig.focusX, this.cameraRig.focusZ, time, this.cameraRig.camera.quaternion);
    this.guns.updateShimmer(time);
    this.updateAura(view, alpha, dt, time);
    this.fx.update(dt);
    this.shotStyle = this.fxSelf ? view.shotFxStyle() : null;
    this.projectiles.forceTrail = this.shotFx.wantsTrail;
    this.shotFx.update(dt, this.cameraRig.camera);
    this.cameraRig.update(dt);
    this.post.render();
    this.watchFps(dt);
  }

  /** Bloom actif mais affichage durablement sous ~45 i/s : on le coupe pour la session (degradation douce, sans a-coup). */
  private watchFps(dt: number): void {
    if (!this.post.active) {
      this.fpsGuard.pause();
      return;
    }
    if (!this.fpsGuard.sample(dt)) return;
    this.autoLow = true;
    this.post.setEnabled(false);
    this.onAutoLowQuality?.();
  }

  /** Aura du chef : suit sa position interpolee (meme formule que EntityViews), disparait en fondu quand elle est coupee. */
  private updateAura(view: SimView, alpha: number, dt: number, time: number): void {
    const leaderId = view.catIds[0];
    const leader = leaderId !== undefined ? view.entity(leaderId) : undefined;
    const style = this.fxSelf && leader?.alive && leader.cat ? view.auraStyle() : null;
    let x = 0;
    let z = 0;
    if (leader) {
      const a = Math.min(1, Math.max(0, alpha));
      const t = leader.transform;
      x = t.prevX + (t.x - t.prevX) * a;
      z = -(t.prevY + (t.y - t.prevY) * a);
    }
    // aura « orbites » (gabarit rig) : dessinee par AuraRig ; les autres par AuraRenderer
    this.aura.update(style?.rig ? null : style, x, z, time, dt, this.cameraRig.camera.quaternion);
    this.auraRig.update(style, x, z, dt, this.cameraRig.camera);
    // auras des autres joueurs (hub) : positions lissees de EntityViews, coordonnees Three (z = -y)
    const list = this.remoteAuraList;
    list.length = 0;
    if (this.fxOthers && view.mode === 'hub' && this.remoteAuraIds.size > 0) {
      this.views.forEachRemote((id, rx, ry) => {
        const auraId = this.remoteAuraIds.get(id);
        if (auraId) list.push({ id, x: rx, z: -ry, style: this.opts.index.aura(auraId).style });
      });
    }
    this.remoteAuras.update(list, x, z, time, dt, this.cameraRig.camera);
  }

  worldToScreen(x: number, y: number, h: number): ScreenPoint {
    this.tmp.set(x, h, -y).project(this.cameraRig.camera);
    return {
      x: (this.tmp.x * 0.5 + 0.5) * this.cssW,
      y: (-this.tmp.y * 0.5 + 0.5) * this.cssH,
      visible: this.tmp.z > -1 && this.tmp.z < 1,
    };
  }

  /** Diagnostic de fuite GPU (ACCEPTANCE : stable apres 20 changements de mode/biome). */
  memory(): GpuMemoryInfo {
    const i = this.renderer.info;
    return { geometries: i.memory.geometries, textures: i.memory.textures, programs: i.programs?.length ?? 0, tracked: this.res.count(), drawCalls: i.render.calls };
  }

  dispose(): void {
    this.aura.dispose();
    this.auraRig.dispose();
    this.remoteAuras.dispose();
    this.shotFx.dispose();
    this.places.dispose();
    this.ambience.dispose();
    this.fx.dispose();
    this.views.dispose();
    this.ground.dispose();
    this.post.dispose();
    this.res.releaseAll();
    this.renderer.dispose();
  }
}
